import { useEffect, useRef } from "react";

import { API } from "./api";
import { showToast } from "./helper";

/**
 * Handles the return leg of a redirect-based Stripe payment (Cash App Pay,
 * Amazon Pay, bank redirects). Stripe sends the user back to `return_url`
 * with `redirect_status` + `payment_intent` params plus our own
 * `paymentBookingId`. On mount we resolve the booking, confirm or record
 * the payment, then strip the params so a refresh can't re-trigger it.
 *
 * @param {Function} onResolved - called after a successful confirm so the
 *   caller can refresh its list / state.
 */
const usePaymentRedirectReturn = (onResolved) => {
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get("redirect_status");
    const bookingId = params.get("paymentBookingId");

    if (!redirectStatus || !bookingId) return;
    handledRef.current = true;

    const clearParams = () => {
      const url = new URL(window.location.href);
      ["redirect_status", "payment_intent", "payment_intent_client_secret", "paymentBookingId"].forEach(
        (k) => url.searchParams.delete(k),
      );
      window.history.replaceState({}, "", url.toString());
    };

    if (redirectStatus !== "succeeded") {
      // Pull the internal paymentId so the failure is recorded against it.
      API.getSingleBooking(bookingId)
        .then((res) => {
          const paymentId =
            res && res.data && res.data.payment && res.data.payment.id;
          if (paymentId) {
            return API.failedBookingPayment({
              paymentId,
              bookingId,
              errorMessage: `Payment ${redirectStatus}`,
            });
          }
        })
        .catch(() => {})
        .finally(() => {
          showToast(
            "error",
            redirectStatus === "failed"
              ? "Payment failed. Please try again."
              : "Payment was not completed.",
          );
          clearParams();
        });
      return;
    }

    // Succeeded: resolve the internal paymentId, then confirm the booking.
    API.getSingleBooking(bookingId)
      .then((res) => {
        const paymentId =
          res && res.data && res.data.payment && res.data.payment.id;
        if (!paymentId) {
          showToast(
            "error",
            "Payment completed but the booking could not be matched. Please contact support.",
          );
          return;
        }
        return API.confirmPayment({ paymentId, bookingId }).then((cres) => {
          if (cres && cres.type === "success") {
            showToast(
              "success",
              "Payment Successful : Your booking has been confirmed!",
            );
            if (typeof onResolved === "function") onResolved();
          } else {
            showToast(
              "error",
              (cres && cres.message) ||
                "Payment completed but we couldn't update your booking. Please contact support.",
            );
          }
        });
      })
      .catch(() => {
        showToast(
          "error",
          "Payment completed but we couldn't update your booking. Please contact support.",
        );
      })
      .finally(() => clearParams());
  }, [onResolved]);
};

export default usePaymentRedirectReturn;
